import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { APPROVAL_GATES, isApproved } from "@/payroll/approvals/approval-types";
import type { IPayrollSheetApprovalStatus } from "@/payroll/payroll.d";

/**
 * A group of tabs that reruns together.
 *
 * The access key is the unit, because it is already the unit of SIGN-OFF: the
 * approval fingerprint is computed across every sheet sharing a key, so
 * rerunning half a key drops the whole key's approval anyway. Rerunning a subset
 * therefore buys nothing and only makes the result harder to explain.
 */
export interface RerunUnit {
  /** Stable id — the access key, or the sheet id for an ungrouped tab. */
  id: string;
  name: string;
  /** Every tab in the unit, for describing what the rerun covers. */
  sheetNames: string[];
  /**
   * The tabs actually sent as the run's scope: the self-contained ones. Tabs
   * that total up others are left out on purpose — the API refuses them ("totals
   * up other tabs, so it cannot be rerun on its own") and regenerates every one
   * of them on any scoped run regardless, so naming them would be both rejected
   * and redundant.
   */
  rerunSheetNames: string[];
  /** Tabs in this unit already running or queued. */
  busySheetNames: string[];
  /** Whether rerunning would withdraw a sign-off this unit currently holds. */
  hasApprovals: boolean;
}

const unitKeyFor = (sheet: IAiSpreadsheetSheet) =>
  sheet.access_key ?? `sheet:${sheet.id}`;

/**
 * The units offered for a scoped rerun, in workbook order.
 *
 * A unit is offered only when it holds at least one self-contained tab. A key
 * that is nothing but aggregating tabs (a Summary on its own) is left out: it
 * recalculates on every run already, so "rerun just this" would be a promise
 * with no meaning behind it.
 */
export const getRerunUnits = ({
  sheets,
  approvalStatus,
  busySheetNames = [],
}: {
  sheets: IAiSpreadsheetSheet[];
  approvalStatus?: IPayrollSheetApprovalStatus | null;
  busySheetNames?: string[];
}): RerunUnit[] => {
  const approvalByKey = new Map(
    (approvalStatus?.access_keys ?? []).map((accessKey) => [
      accessKey.key,
      accessKey,
    ]),
  );
  const byUnit = new Map<string, IAiSpreadsheetSheet[]>();

  sheets.forEach((sheet) => {
    const id = unitKeyFor(sheet);
    byUnit.set(id, [...(byUnit.get(id) ?? []), sheet]);
  });

  return Array.from(byUnit.entries())
    .map(([id, unitSheets]) => {
      const accessKey = unitSheets[0].access_key;
      const approval = accessKey ? approvalByKey.get(accessKey) : undefined;
      const rerunnable = unitSheets.filter(
        (sheet) => sheet.rerun_scopable && sheet.editable,
      );

      return {
        id,
        // The access key's own name where the business has one; otherwise the
        // tab speaks for itself.
        name: approval?.name ?? unitSheets[0].name,
        sheetNames: unitSheets.map((sheet) => sheet.name),
        rerunSheetNames: rerunnable.map((sheet) => sheet.name),
        busySheetNames: unitSheets
          .map((sheet) => sheet.name)
          .filter((name) => busySheetNames.includes(name)),
        hasApprovals: Boolean(
          approval &&
            APPROVAL_GATES.some((gate) => isApproved(approval, gate.type)),
        ),
      };
    })
    .filter((unit) => unit.rerunSheetNames.length > 0);
};

/** The scope to send for a set of chosen units, deduped and free of busy tabs. */
export const getRerunScope = (units: RerunUnit[]): string[] =>
  Array.from(
    new Set(
      units.flatMap((unit) =>
        unit.rerunSheetNames.filter(
          (name) => !unit.busySheetNames.includes(name),
        ),
      ),
    ),
  );

/** Tabs a rerun of these units would regenerate beyond the ones it names. */
export const getCarriedAlongSheetNames = (unit: RerunUnit): string[] =>
  unit.sheetNames.filter((name) => !unit.rerunSheetNames.includes(name));

/**
 * Whether a unit's only rerun tab is called after the unit itself.
 *
 * Most groups hold one tab that shares their name, so spelling it out under the
 * heading prints the same words twice and buries the groups where the tab list
 * genuinely tells you something. Multiple tabs, or a tab named differently from
 * its group, still earn the line.
 */
export const namesOwnSheet = (unit: RerunUnit): boolean =>
  unit.rerunSheetNames.length === 1 && unit.rerunSheetNames[0] === unit.name;
